/**
 * N5 — Génère une image partageable du bilan de mandat.
 *
 * Canvas 1200×630 (format Open Graph / Twitter card). Brutaliste :
 * fond papier, encadré ink, archétype en typo display, 3 stats numéro,
 * citation italique, branding moipresident.io.
 */

export interface ShareImageInput {
  cause: string;
  pretitle: string;
  archetype: string;
  closing: string;
  stats: { approval: number; debtRatio: number; balance: number };
}

const W = 1200;
const H = 630;

export function renderShareImage(input: ShareImageInput): string {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  // Fond papier.
  ctx.fillStyle = "#f5f1e8";
  ctx.fillRect(0, 0, W, H);

  // Bordure ink monumentale (brutalisme).
  ctx.fillStyle = "#1a1a1a";
  const borderInset = 24;
  const borderThickness = 6;
  // top/bottom
  ctx.fillRect(borderInset, borderInset, W - borderInset * 2, borderThickness);
  ctx.fillRect(
    borderInset,
    H - borderInset - borderThickness,
    W - borderInset * 2,
    borderThickness,
  );
  // left/right
  ctx.fillRect(borderInset, borderInset, borderThickness, H - borderInset * 2);
  ctx.fillRect(
    W - borderInset - borderThickness,
    borderInset,
    borderThickness,
    H - borderInset * 2,
  );

  // Pré-titre (cause + date).
  ctx.fillStyle = "#666";
  ctx.font = "700 22px ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
  ctx.textAlign = "left";
  ctx.fillText(input.pretitle, 80, 110);

  // Archétype en typo display monumentale.
  // Couleur rouge pour fin politique anticipée (motion/coup), ink sinon.
  const isPoliticalDefeat =
    input.cause === "motion_censure" ||
    input.cause === "coup_etat" ||
    input.cause === "trahison_promesse";
  ctx.fillStyle = isPoliticalDefeat ? "#c62828" : "#1a1a1a";
  ctx.font = "900 78px Georgia, 'Times New Roman', serif";
  wrapText(ctx, input.archetype, 80, 200, W - 160, 88);

  // Citation de clôture en italique.
  ctx.fillStyle = "#333";
  ctx.font = "italic 24px Georgia, 'Times New Roman', serif";
  wrapText(ctx, `« ${input.closing} »`, 80, 320, W - 160, 32);

  // 3 stats (cote / dette / solde).
  const statY = 470;
  const statSpacing = (W - 160) / 3;
  drawStat(
    ctx,
    "COTE FINALE",
    `${Math.round(input.stats.approval)} %`,
    80,
    statY,
  );
  drawStat(
    ctx,
    "DETTE / PIB",
    `${input.stats.debtRatio.toFixed(0)} %`,
    80 + statSpacing,
    statY,
  );
  const balance = input.stats.balance;
  const balanceStr = (balance >= 0 ? "+" : "") + balance.toFixed(0) + " Md€";
  drawStat(ctx, "SOLDE FINAL", balanceStr, 80 + statSpacing * 2, statY);

  // Branding bas-droit.
  ctx.fillStyle = "#666";
  ctx.font = "700 18px ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
  ctx.textAlign = "right";
  ctx.fillText("Moi Président · moipresident.io", W - 80, H - 60);

  return canvas.toDataURL("image/png");
}

function drawStat(
  ctx: CanvasRenderingContext2D,
  label: string,
  value: string,
  x: number,
  y: number,
) {
  ctx.fillStyle = "#666";
  ctx.font = "700 16px ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
  ctx.textAlign = "left";
  ctx.fillText(label, x, y);

  ctx.fillStyle = "#1a1a1a";
  ctx.font = "900 44px ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
  ctx.fillText(value, x, y + 50);
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
) {
  const words = text.split(" ");
  let line = "";
  let curY = y;
  for (let i = 0; i < words.length; i++) {
    const test = line + words[i] + " ";
    const w = ctx.measureText(test).width;
    if (w > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, curY);
      line = words[i] + " ";
      curY += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, curY);
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
